import { createProjectMasonry } from './project-masonry.js';

const decodeFilters = (card) => {
    try {
        return JSON.parse(card.dataset.projectFilters || '{}');
    } catch (error) {
        return {};
    }
};

const twoFrames = (callback) => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(callback));
};

const easeOutQuart = (t) => 1 - ((1 - t) ** 4);

export const calculateProjectFilterSafeScroll = ({
    listingTop,
    contentBottom,
    viewportHeight,
    dockHeight,
}) => {
    const lowerClearance = Math.max(88, dockHeight + 36);
    return Math.max(
        0,
        listingTop - 28,
        contentBottom - viewportHeight + lowerClearance,
    );
};

export function initProjectsArchive({ gsap = null, ScrollTrigger = null, reducedMotion = false, lenis = null } = {}) {
    const root = document.querySelector('[data-project-archive]');
    if (!root) return;

    const body = document.body;
    const form = root.querySelector('[data-project-filters]');
    const cards = [...root.querySelectorAll('[data-project-card]')];
    const grid = root.querySelector('[data-project-grid]');
    const empty = root.querySelector('[data-project-empty]');
    const listing = root.querySelector('[data-project-listing]');
    const resultCount = root.querySelector('[data-project-results-count]');
    const activeCount = root.querySelector('[data-project-active-count]');
    const openButton = root.querySelector('[data-project-filter-open]');
    const closeButton = root.querySelector('[data-project-filter-close]');
    const applyButton = root.querySelector('[data-project-filter-apply]');
    const clearButtons = [...root.querySelectorAll('[data-project-filter-clear], [data-project-filter-mobile-clear]')];
    const groups = [...root.querySelectorAll('[data-project-filter-group]')];
    const checkboxes = [...root.querySelectorAll('input[type="checkbox"]')];
    const viewButtons = [...root.querySelectorAll('[data-project-view]')];
    const cityView = root.querySelector('[data-project-city-view]');
    const cityExplorer = cityView?.querySelector('[data-city-explorer]');
    const mapView = root.querySelector('[data-project-map-view]');
    let mapController = null;
    let mapModulePromise = null;

    const ensureMapController = async () => {
        if (mapController || !mapView) return mapController;
        mapModulePromise ||= import('./projects-map.js');
        const { initProjectsMap } = await mapModulePromise;
        mapController = initProjectsMap(mapView);
        return mapController;
    };
    const mapClose = mapView?.querySelector('[data-project-map-close]');
    const filterRegion = root.querySelector('[data-project-filter-region]');
    const footerSpacer = document.querySelector('[data-footer-reveal-spacer]');

    let currentView = 'grid';
    let footerZone = false;
    let menuOpen = body.classList.contains('menu-is-open');
    let filterDockVisible = false;
    let footerDockTrigger = null;
    let footerObserver = null;
    let bodyObserver = null;
    let filterIntroPlayed = false;
    let filterScrollTimer = 0;
    let filterStabilizationId = 0;
    const cardRevealTriggers = new Set();

    const closePanels = (except = null) => {
        groups.forEach((group) => {
            if (group === except) return;
            const toggle = group.querySelector('[data-project-filter-toggle]');
            const panel = group.querySelector('[data-project-filter-panel]');
            if (toggle) toggle.setAttribute('aria-expanded', 'false');
            if (panel) panel.hidden = true;
        });
    };

    const isMobileFilterOpen = () => (
        form?.dataset.mobileOpen === 'true'
        && window.matchMedia('(max-width: 767px)').matches
    );

    const shouldShowFilterDock = () => (
        currentView === 'grid'
        && !footerZone
        && !menuOpen
    );

    const setFilterDockVisible = (visible, { immediate = false, intro = false } = {}) => {
        if (!filterRegion) return;
        const nextVisible = Boolean(visible);
        const sameState = nextVisible === filterDockVisible;

        filterDockVisible = nextVisible;
        filterRegion.dataset.projectFilterVisible = String(nextVisible);
        filterRegion.toggleAttribute('inert', !nextVisible);
        filterRegion.setAttribute('aria-hidden', nextVisible ? 'false' : 'true');

        if (!nextVisible) closePanels();

        if (!gsap || reducedMotion || immediate) {
            if (gsap) gsap.killTweensOf(filterRegion);
            filterRegion.style.opacity = nextVisible ? '1' : '0';
            filterRegion.style.visibility = nextVisible ? 'visible' : 'hidden';
            filterRegion.style.transform = nextVisible ? 'translate3d(0,0,0)' : 'translate3d(0,28px,0)';
            filterRegion.style.pointerEvents = nextVisible ? 'auto' : 'none';
            return;
        }

        if (sameState && !intro) return;

        gsap.killTweensOf(filterRegion);

        if (nextVisible) {
            filterRegion.style.pointerEvents = 'auto';
            filterRegion.style.visibility = 'visible';
            gsap.fromTo(
                filterRegion,
                { autoAlpha: intro ? 0 : Math.min(Number.parseFloat(getComputedStyle(filterRegion).opacity) || 0, 0.45), y: intro ? 38 : 26 },
                {
                    autoAlpha: 1,
                    y: 0,
                    duration: intro ? 0.78 : 0.58,
                    delay: intro ? 0.12 : 0,
                    ease: 'power3.out',
                    overwrite: true,
                    clearProps: 'transform',
                },
            );
            return;
        }

        filterRegion.style.pointerEvents = 'none';
        gsap.to(filterRegion, {
            autoAlpha: 0,
            y: 30,
            duration: 0.38,
            ease: 'power3.in',
            overwrite: true,
            onComplete: () => {
                if (filterDockVisible) return;
                filterRegion.style.visibility = 'hidden';
            },
        });
    };

    const syncFilterDock = ({ immediate = false, intro = false } = {}) => {
        setFilterDockVisible(shouldShowFilterDock(), { immediate, intro });
    };

    let mapTransitioning = false;

    const openMapView = async () => {
        if (!mapView || mapTransitioning) return;
        mapTransitioning = true;
        currentView = 'map';
        closePanels();
        viewButtons.forEach((button) => {
            const active = button.dataset.projectView === 'map';
            button.dataset.active = String(active);
            button.setAttribute('aria-pressed', String(active));
        });
        mapView.hidden = false;
        await ensureMapController();
        mapView.style.visibility = 'visible';
        mapView.style.pointerEvents = 'auto';
        lenis?.stop?.();
        body.classList.add('projects-map-is-open');
        syncFilterDock();

        const finish = () => {
            mapTransitioning = false;
            mapController?.refresh?.();
            window.requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
        };
        if (!gsap || reducedMotion) {
            mapView.style.clipPath = 'inset(0 0 0% 0)';
            finish();
            return;
        }
        gsap.killTweensOf(mapView);
        gsap.fromTo(mapView,
            { clipPath: 'inset(0 0 100% 0)' },
            { clipPath: 'inset(0 0 0% 0)', duration: 0.9, ease: 'power4.inOut', overwrite: true, onComplete: finish },
        );
    };

    const closeMapView = () => {
        if (!mapView || mapView.hidden || mapTransitioning) return;
        mapTransitioning = true;
        const finish = () => {
            mapView.hidden = true;
            mapView.style.visibility = 'hidden';
            mapView.style.pointerEvents = 'none';
            mapView.style.clipPath = 'inset(0 0 100% 0)';
            body.classList.remove('projects-map-is-open');
            currentView = 'grid';
            viewButtons.forEach((button) => {
                const active = button.dataset.projectView === 'grid';
                button.dataset.active = String(active);
                button.setAttribute('aria-pressed', String(active));
            });
            lenis?.start?.();
            mapTransitioning = false;
            syncFilterDock();
            window.requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
        };
        if (!gsap || reducedMotion) { finish(); return; }
        gsap.killTweensOf(mapView);
        gsap.to(mapView, { clipPath: 'inset(0 0 100% 0)', duration: 0.78, ease: 'power4.inOut', overwrite: true, onComplete: finish });
    };

    const setView = (view) => {
        if (view === 'map') { void openMapView(); return; }
        if (currentView === 'map') { closeMapView(); return; }
        const isCity = view === 'city';
        viewButtons.forEach((button) => {
            const active = button.dataset.projectView === view;
            button.dataset.active = String(active);
            button.setAttribute('aria-pressed', String(active));
        });
        currentView = isCity ? 'city' : 'grid';
        if (cityView) cityView.hidden = !isCity;
        if (listing) listing.hidden = isCity;
        if (!isCity) cityExplorer?.dispatchEvent(new CustomEvent('city:deactivate'));
        if (isCity) {
            closePanels();
            window.requestAnimationFrame(() => cityExplorer?.dispatchEvent(new CustomEvent('city:activate')));
        }
        syncFilterDock();
        window.requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    };
    viewButtons.forEach((button) => button.addEventListener('click', () => setView(button.dataset.projectView || 'grid')));
    cityExplorer?.addEventListener('city:closed', () => setView('grid'));
    mapClose?.addEventListener('click', closeMapView);
    window.addEventListener('keydown', (event) => { if (event.key === 'Escape' && currentView === 'map') closeMapView(); });

    if (!form || !cards.length || !grid || !listing) return;

    const masonry = createProjectMasonry(grid, { breakpoint: 1024, gutter: 36, columns: 3, reducedMotion });
    let navigationInProgress = false;

    const lockArchiveForNavigation = (event) => {
        const link = event.target.closest('[data-project-card] a[href]');
        if (!link || navigationInProgress) return;
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (link.target && link.target !== '_self') return;

        navigationInProgress = true;
        root.dataset.projectNavigating = 'true';
        killCardRevealTriggers();
        if (gsap) {
            gsap.killTweensOf(cards);
            gsap.set(cards, { autoAlpha: 1, clearProps: 'opacity,visibility' });
        }
        masonry?.freeze?.();
    };

    grid.addEventListener('click', lockArchiveForNavigation, { capture: true });
    const mobileFilterPlaceholder = document.createComment('led-project-filter-form-home');
    form.parentNode?.insertBefore(mobileFilterPlaceholder, form);

    const mountMobileFilterPortal = () => {
        if (!window.matchMedia('(max-width: 767px)').matches) return;
        if (form.parentNode !== body) body.appendChild(form);
    };

    const restoreMobileFilterPortal = () => {
        const home = mobileFilterPlaceholder.parentNode;
        if (!home || form.parentNode === home) return;
        home.insertBefore(form, mobileFilterPlaceholder.nextSibling);
    };

    const killCardRevealTriggers = () => {
        cardRevealTriggers.forEach((trigger) => trigger.kill());
        cardRevealTriggers.clear();
        if (gsap) gsap.killTweensOf(cards);
    };

    const revealCard = (card, delay = 0) => {
        if (!gsap || reducedMotion) {
            card.dataset.projectRevealed = 'true';
            card.style.removeProperty('opacity');
            card.style.removeProperty('visibility');
            return;
        }

        gsap.to(card, {
            autoAlpha: 1,
            duration: 0.50,
            delay,
            ease: 'power3.out',
            overwrite: 'auto',
            onComplete: () => {
                card.dataset.projectRevealed = 'true';
                gsap.set(card, { clearProps: 'opacity,visibility' });
            },
        });
    };

    const setupCardReveals = (visibleCards, {
        filtered = false,
        enteringCards = new Set(),
        skipKill = false,
        deferRefresh = false,
    } = {}) => {
        if (!skipKill) killCardRevealTriggers();

        if (!gsap || !ScrollTrigger || reducedMotion) {
            visibleCards.forEach((card) => {
                card.dataset.projectRevealed = 'true';
                card.style.removeProperty('opacity');
                card.style.removeProperty('visibility');
            });
            if (!deferRefresh) {
                ScrollTrigger?.refresh?.();
                lenis?.resize?.();
            }
            return;
        }

        // Trigger slightly before the card reaches the viewport so the user sees
        // the card settle into full opacity rather than scrolling through a dim
        // card and waiting for the animation to catch up.
        const viewportThreshold = window.innerHeight + 120;
        const immediate = [];

        visibleCards.forEach((card) => {
            const rect = card.getBoundingClientRect();
            const entering = enteringCards.has(card);
            const alreadyRevealed = card.dataset.projectRevealed === 'true' && !entering;

            if (rect.bottom <= 0 || alreadyRevealed) {
                gsap.set(card, { autoAlpha: 1, clearProps: 'opacity,visibility' });
                card.dataset.projectRevealed = 'true';
                return;
            }

            gsap.set(card, { autoAlpha: entering ? 0 : 0.52 });

            if (rect.top <= viewportThreshold) {
                immediate.push(card);
                return;
            }

            const trigger = ScrollTrigger.create({
                trigger: card,
                start: 'top bottom+=120',
                once: true,
                onEnter: () => revealCard(card),
            });
            cardRevealTriggers.add(trigger);
        });

        immediate.forEach((card, index) => {
            revealCard(card, index * (filtered ? 0.025 : 0.04));
        });

        if (!deferRefresh) {
            ScrollTrigger.refresh();
            lenis?.resize?.();
        }
    };

    const finishListingStabilization = (stabilizationId = filterStabilizationId) => {
        if (stabilizationId !== filterStabilizationId) return;
        if (filterScrollTimer) {
            window.clearTimeout(filterScrollTimer);
            filterScrollTimer = 0;
        }
        listing.style.removeProperty('min-height');
        lenis?.resize?.();
        ScrollTrigger?.refresh?.();
    };

    const smoothScrollTo = (targetY, stabilizationId) => {
        const target = Math.max(0, Math.round(targetY));
        let finished = false;
        const done = () => {
            if (finished) return;
            finished = true;
            finishListingStabilization(stabilizationId);
        };

        if (lenis?.scrollTo) {
            lenis.scrollTo(target, {
                duration: 1.08,
                easing: easeOutQuart,
                force: true,
                onComplete: done,
            });
            filterScrollTimer = window.setTimeout(done, 1400);
            return;
        }

        window.scrollTo({ top: target, behavior: reducedMotion ? 'auto' : 'smooth' });
        filterScrollTimer = window.setTimeout(done, reducedMotion ? 50 : 820);
    };

    const stabilizeViewportAfterFilter = (visibleCards, stabilizationId) => {
        const scrollY = window.scrollY || window.pageYOffset || 0;
        const listingRect = listing.getBoundingClientRect();
        const listingTop = listingRect.top + scrollY;
        const candidates = visibleCards.length ? visibleCards : (empty && !empty.hidden ? [empty] : [grid]);
        const contentBottom = Math.max(
            listingTop,
            ...candidates.map((element) => element.getBoundingClientRect().bottom + scrollY),
        );
        const dockHeight = filterDockVisible && filterRegion
            ? filterRegion.getBoundingClientRect().height
            : 0;
        const safeMaxScroll = calculateProjectFilterSafeScroll({
            listingTop,
            contentBottom,
            viewportHeight: window.innerHeight,
            dockHeight,
        });

        if (scrollY > safeMaxScroll + 12) {
            smoothScrollTo(safeMaxScroll, stabilizationId);
            return;
        }

        if (reducedMotion || !gsap) {
            finishListingStabilization(stabilizationId);
            return;
        }

        // Keep the old document height only for the short FLIP/fade window.
        // Releasing it after the cards settle prevents both browser scroll-clamp
        // jumps and the lingering empty-page feeling of the previous version.
        filterScrollTimer = window.setTimeout(
            () => finishListingStabilization(stabilizationId),
            720,
        );
    };

    const cardFilters = new Map(cards.map((card) => [card, decodeFilters(card)]));
    const params = new URLSearchParams(window.location.search);

    groups.forEach((group) => {
        const key = group.dataset.projectFilterGroup;
        const selected = (params.get(key) || '').split(',').filter(Boolean);
        group.querySelectorAll('input[type="checkbox"]').forEach((input) => {
            input.checked = selected.includes(input.value);
        });
    });

    const selectedByGroup = () => {
        const selected = {};
        groups.forEach((group) => {
            const key = group.dataset.projectFilterGroup;
            selected[key] = [...group.querySelectorAll('input:checked')].map((input) => input.value);
        });
        return selected;
    };

    const updateSummaries = (selected) => {
        let total = 0;
        groups.forEach((group) => {
            const key = group.dataset.projectFilterGroup;
            const summary = group.querySelector('[data-project-filter-summary]');
            const inputs = [...group.querySelectorAll('input:checked')];
            const labels = inputs.map((input) => input.dataset.label || input.value);
            total += labels.length;

            if (!summary) return;
            if (!labels.length) summary.textContent = summary.dataset.defaultLabel || key.toUpperCase();
            else if (labels.length === 1) summary.textContent = labels[0];
            else summary.textContent = `${labels.length} selected`;
        });

        if (activeCount) {
            activeCount.textContent = String(total);
            activeCount.hidden = total === 0;
        }
        clearButtons.forEach((button) => {
            if (button.matches('[data-project-filter-clear]')) button.hidden = total === 0;
        });
        return total;
    };

    const updateUrl = (selected) => {
        const url = new URL(window.location.href);
        Object.entries(selected).forEach(([key, values]) => {
            if (values.length) url.searchParams.set(key, values.join(','));
            else url.searchParams.delete(key);
        });
        window.history.replaceState({}, '', url);
    };

    const updateResultCount = (count) => {
        if (!resultCount) return;
        const singular = resultCount.dataset.projectCountSingular || 'Project';
        const plural = resultCount.dataset.projectCountPlural || 'Projects';
        resultCount.textContent = `${count} ${count === 1 ? singular : plural}`;
    };

    const applyFilters = ({ updateHistory = true, animate = true } = {}) => {
        if (navigationInProgress) return;
        const stabilizationId = ++filterStabilizationId;
        if (filterScrollTimer) {
            window.clearTimeout(filterScrollTimer);
            filterScrollTimer = 0;
            lenis?.scrollTo?.(window.scrollY || window.pageYOffset || 0, { immediate: true, force: true });
        }

        const selected = selectedByGroup();
        const visibleCards = [];
        const enteringCards = new Set();
        const exitingCards = [];
        const survivors = [];
        const nextVisibility = new Map();

        cards.forEach((card) => {
            const filters = cardFilters.get(card) || {};
            const matches = Object.entries(selected).every(([group, values]) => {
                if (!values.length) return true;
                const cardValues = Array.isArray(filters[group]) ? filters[group] : [];
                return values.some((value) => cardValues.includes(value));
            });
            const wasVisible = !card.hidden;
            nextVisibility.set(card, matches);

            if (matches) {
                visibleCards.push(card);
                if (wasVisible) survivors.push(card);
                else enteringCards.add(card);
            } else if (wasVisible) {
                exitingCards.push(card);
            }
        });

        killCardRevealTriggers();

        const previousListingHeight = Math.ceil(listing.getBoundingClientRect().height);
        if (animate && previousListingHeight > 0) listing.style.minHeight = `${previousListingHeight}px`;

        // Never dim cards that are already on screen just because the filter changed.
        if (gsap) {
            survivors.forEach((card) => {
                if (card.dataset.projectRevealed === 'true') {
                    gsap.set(card, { autoAlpha: 1, clearProps: 'opacity,visibility' });
                }
            });
        }

        const commitLayout = () => {
            if (stabilizationId !== filterStabilizationId) return;

            cards.forEach((card) => {
                const matches = nextVisibility.get(card);
                if (!matches) {
                    card.hidden = true;
                    card.setAttribute('aria-hidden', 'true');
                    if (gsap) gsap.set(card, { clearProps: 'opacity,visibility' });
                    return;
                }

                // Prepare newly matching cards BEFORE removing hidden so there is
                // never a full-opacity paint between filtering and reveal.
                if (enteringCards.has(card) && gsap && !reducedMotion) {
                    gsap.set(card, { autoAlpha: 0, force3D: true });
                    card.dataset.projectRevealed = 'false';
                }
                card.hidden = false;
                card.setAttribute('aria-hidden', 'false');
            });

            updateResultCount(visibleCards.length);
            if (empty) empty.hidden = visibleCards.length !== 0;
            updateSummaries(selected);
            if (updateHistory) updateUrl(selected);

            const afterLayout = () => {
                if (stabilizationId !== filterStabilizationId) return;
                stabilizeViewportAfterFilter(visibleCards, stabilizationId);
            };

            // masonry-layout owns all desktop x/y placement from here. It keeps
            // persistent item instances and ignore/unignore state, so repeated
            // multi-filter/reset cycles cannot retain stale CSS grid rows.
            masonry?.layout({ immediate: !animate, onComplete: afterLayout });

            window.requestAnimationFrame(() => {
                if (stabilizationId !== filterStabilizationId) return;
                setupCardReveals(visibleCards, {
                    filtered: animate,
                    enteringCards,
                    skipKill: true,
                    deferRefresh: true,
                });
            });
        };

        if (animate && gsap && !reducedMotion && exitingCards.length) {
            gsap.to(exitingCards, {
                autoAlpha: 0,
                duration: 0.16,
                stagger: 0.012,
                ease: 'power2.out',
                overwrite: true,
                onComplete: commitLayout,
            });
            return;
        }

        commitLayout();
    };

    groups.forEach((group) => {
        const toggle = group.querySelector('[data-project-filter-toggle]');
        const panel = group.querySelector('[data-project-filter-panel]');
        if (!toggle || !panel) return;

        toggle.addEventListener('click', () => {
            const willOpen = toggle.getAttribute('aria-expanded') !== 'true';
            closePanels(group);
            toggle.setAttribute('aria-expanded', String(willOpen));
            panel.hidden = !willOpen;
        });
    });

    checkboxes.forEach((input) => input.addEventListener('change', () => {
        if (isMobileFilterOpen()) {
            updateSummaries(selectedByGroup());
            return;
        }
        applyFilters();
    }));

    const clearFilters = () => {
        checkboxes.forEach((input) => {
            input.checked = false;
        });

        if (isMobileFilterOpen()) {
            updateSummaries(selectedByGroup());
            return;
        }
        applyFilters();
    };
    clearButtons.forEach((button) => button.addEventListener('click', clearFilters));

    const openMobileFilters = () => {
        closePanels();
        mountMobileFilterPortal();
        form.dataset.mobileOpen = 'true';
        form.setAttribute('aria-hidden', 'false');
        body.classList.add('project-filter-is-open');
        lenis?.stop?.();
        openButton?.setAttribute('aria-expanded', 'true');

        if (gsap && !reducedMotion) {
            gsap.killTweensOf(form);
            gsap.fromTo(
                form,
                { autoAlpha: 0, y: 28 },
                { autoAlpha: 1, y: 0, duration: 0.46, ease: 'power3.out', clearProps: 'opacity,visibility,transform' },
            );
        }

        window.requestAnimationFrame(() => closeButton?.focus({ preventScroll: true }));
    };

    const closeMobileFilters = ({ restoreFocus = true, onComplete = null } = {}) => {
        if (form.dataset.mobileOpen !== 'true') {
            restoreMobileFilterPortal();
            onComplete?.();
            return;
        }

        closePanels();
        openButton?.setAttribute('aria-expanded', 'false');
        form.setAttribute('aria-hidden', 'true');
        body.classList.remove('project-filter-is-open');

        const finish = () => {
            form.dataset.mobileOpen = 'false';
            if (gsap) gsap.set(form, { clearProps: 'opacity,visibility,transform' });
            restoreMobileFilterPortal();
            if (!body.classList.contains('menu-is-open') && !body.classList.contains('search-is-open') && !body.classList.contains('city-explorer-is-active')) {
                lenis?.start?.();
            }
            if (restoreFocus) openButton?.focus({ preventScroll: true });
            onComplete?.();
        };

        if (gsap && !reducedMotion) {
            gsap.killTweensOf(form);
            gsap.to(form, {
                autoAlpha: 0,
                y: 20,
                duration: 0.28,
                ease: 'power2.in',
                overwrite: true,
                onComplete: finish,
            });
            return;
        }

        finish();
    };

    openButton?.addEventListener('click', openMobileFilters);
    closeButton?.addEventListener('click', () => closeMobileFilters());
    applyButton?.addEventListener('click', () => {
        closeMobileFilters({ restoreFocus: false, onComplete: () => applyFilters() });
    });

    document.addEventListener('click', (event) => {
        if (!event.target.closest('[data-project-filter-group]')) closePanels();
    });
    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        closePanels();
        if (form.dataset.mobileOpen === 'true') closeMobileFilters();
        if (currentView === 'city') setView('grid');
    });

    if (footerSpacer) {
        const syncFooterZoneFromGeometry = () => {
            const rect = footerSpacer.getBoundingClientRect();
            const nextFooterZone = rect.top <= window.innerHeight && rect.bottom >= 0;
            if (nextFooterZone === footerZone) return;
            footerZone = nextFooterZone;
            syncFilterDock();
        };

        if (ScrollTrigger) {
            footerDockTrigger = ScrollTrigger.create({
                trigger: footerSpacer,
                start: 'top bottom',
                onEnter: () => {
                    footerZone = true;
                    syncFilterDock();
                },
                onLeaveBack: () => {
                    footerZone = false;
                    syncFilterDock();
                },
                onRefresh: syncFooterZoneFromGeometry,
            });
        } else if ('IntersectionObserver' in window) {
            footerObserver = new IntersectionObserver((entries) => {
                const entry = entries[0];
                if (!entry) return;
                const nextFooterZone = entry.isIntersecting;
                if (nextFooterZone === footerZone) return;
                footerZone = nextFooterZone;
                syncFilterDock();
            });
            footerObserver.observe(footerSpacer);
        }

        syncFooterZoneFromGeometry();
    }

    bodyObserver = new MutationObserver(() => {
        const nextMenuOpen = body.classList.contains('menu-is-open');
        if (nextMenuOpen === menuOpen) return;
        menuOpen = nextMenuOpen;

        if (menuOpen && form.dataset.mobileOpen === 'true') {
            closeMobileFilters({ restoreFocus: false });
        }
        syncFilterDock();
    });
    bodyObserver.observe(body, { attributes: true, attributeFilter: ['class'] });

    applyFilters({ updateHistory: false, animate: false });

    if (filterRegion) {
        filterRegion.style.opacity = '0';
        filterRegion.style.visibility = 'hidden';
        filterRegion.style.pointerEvents = 'none';
        filterRegion.style.transform = 'translate3d(0,38px,0)';
        filterRegion.setAttribute('inert', '');
        filterRegion.setAttribute('aria-hidden', 'true');
        filterDockVisible = false;

        twoFrames(() => {
            if (filterIntroPlayed) return;
            filterIntroPlayed = true;
            syncFilterDock({ intro: true });
        });
    }

    window.addEventListener('resize', () => {
        if (window.matchMedia('(min-width: 768px)').matches && form.dataset.mobileOpen !== 'true') {
            restoreMobileFilterPortal();
        }
    }, { passive: true });

    window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        navigationInProgress = false;
        delete root.dataset.projectNavigating;
        masonry?.resume?.();
        syncFilterDock();
        window.requestAnimationFrame(() => ScrollTrigger?.refresh?.());
    });

    window.addEventListener('pagehide', (event) => {
        // Never dismantle Masonry during a link click/beforeunload. Doing so
        // visibly snaps the archive back to its CSS-grid fallback for one frame.
        // When BFCache is used, preserve the live instance for a pixel-stable return.
        if (event.persisted) return;
        killCardRevealTriggers();
        footerDockTrigger?.kill();
        footerObserver?.disconnect();
        bodyObserver?.disconnect();
        if (filterScrollTimer) window.clearTimeout(filterScrollTimer);
    });
}
